import { useQuery } from '@tanstack/react-query'
import type { AppId } from '@tnn/core'
import type { NavCountPart, NavSection } from '@tnn/ui'
import type { TFunction } from 'i18next'
import { api } from '../api/client'
import type { ResourcePage } from '../resource/config'
import { useMyTasks } from '../tasks/api'

/** Something waiting for me to decide or do in a work app (`GET /api/me/approvals`): how many, and the page that has them */
export interface Approval {
  /** `<app>.<what>` (`accounting.billsToPay`), named by `waiting.<app>_<what>` */
  key: string
  count: number
  app: AppId
  path: string
}

/**
 * One thing waiting, as งานรอทำ lists it (`GET /api/me/approvals/items`): its kind, whose or from whom, its own name when
 * another (a document's number), its page and since when
 */
export interface WaitingItem {
  key: string
  app: AppId
  id: string
  title: string
  subtitle?: string
  path: string
  since: string
}

export const waitingKey = ['me', 'approvals'] as const

/**
 * What waits for me across the apps: Thananan OS's รอคุณ, and the numbers on each app's menu (`waitingOnMenu`). Asked
 * again every minute, and after anything is saved (`createApp`)
 */
export function useWaiting() {
  return useQuery({
    queryKey: waitingKey,
    queryFn: () => api.get<{ items: Approval[] }>('/me/approvals'),
    refetchInterval: 60_000,
  })
}

/** What waits for me one by one, the latest first: งานรอทำ's list (asked again with `useWaiting`, under its key) */
export function useWaitingItems() {
  return useQuery({
    queryKey: [...waitingKey, 'items'],
    queryFn: () => api.get<{ items: WaitingItem[] }>('/me/approvals/items'),
    refetchInterval: 60_000,
  })
}

/**
 * How many things wait for me in each app, by id, for its icon (`AppMark`'s `count`): its approvals, and Thananan OS's
 * also the tasks asked of me on this phone
 */
export function useWaitingByApp(): Partial<Record<AppId, number>> {
  const items = useWaiting().data?.items ?? []
  const tasks = (useMyTasks().data?.assigned ?? []).filter((task) => task.status === 'open').length
  const counts: Partial<Record<AppId, number>> = { portal: tasks }
  for (const item of items) counts[item.app] = (counts[item.app] ?? 0) + item.count
  return counts
}

/** An approval's name, as รอคุณ lists it */
export const waitingLabel = (t: TFunction, key: string) => t(`waiting.${key.replace('.', '_')}`)

/**
 * The numbers what waits puts on this app's menu, by item key: each approval of this app on the item its page opens
 * (the same path, and among several the one whose filters it has), one part per kind, so a menu where one may approve
 * and pay shows the two apart. A part takes the colour of the status its link filters on, as the list's chips show it
 */
export function waitingOnMenu(
  items: Approval[],
  appId: AppId,
  nav: NavSection[],
  pages: ResourcePage[],
  t: TFunction,
): Record<string, NavCountPart[]> {
  const entries = nav.flatMap((s) => s.items).filter((i) => !i.external)
  const parts: Record<string, NavCountPart[]> = {}
  for (const item of items) {
    if (item.app !== appId) continue
    const [path, query] = item.path.split('?')
    const filters = new URLSearchParams(query ?? '')
    const has = (href: string) => [...new URLSearchParams(href.split('?')[1] ?? '')].every(([k, v]) => filters.get(k) === v)
    const same = entries.filter((e) => e.href.split('?')[0] === path)
    const entry = same.find((e) => has(e.href)) ?? same[0]
    if (!entry) continue
    const resource = pages.find((p) => `/${p.resource.path}` === path)?.resource
    const tone = [...filters]
      .filter(([k]) => k.startsWith('f.'))
      .map(([k, v]) => resource?.field(k.slice(2))?.tones?.[v.split(',')[0] ?? ''])
      .find(Boolean)
    parts[entry.key] = [...(parts[entry.key] ?? []), { count: item.count, label: waitingLabel(t, item.key), tone }]
  }
  return parts
}
